import type { HomeyAPI, Device } from 'homey-api';

import { BaseApiStore, type FetchArgs, type ReconnectArgs } from '../BaseApiStore.ts';

export type DeviceProps = {
  [K in keyof Device as Device[K] extends Function ? never : K]: Device[K];
};

export type DevicesData = { [key: string]: Device };
export type DevicesById = {
  [key: string]: DeviceProps;
};

interface State {
  data: DevicesData | null;
  byId: DevicesById | null;
  isLoading: boolean;
  error: unknown;
}

export type DevicesStoreState = State;

export class DevicesStore extends BaseApiStore<State, Device> {
  constructor() {
    super('devices');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.devices, reference: this });

      if (abortController.signal.aborted) return;

      const devicesPromise = api.devices.getDevices({
        $cache: cache,
      });

      const [devicesData] = await Promise.all([devicesPromise]);

      if (abortController.signal.aborted) return;

      const devices = Object.values(devicesData);

      const data: DevicesData = {};
      const byId: DevicesById = {};

      for (const deviceInstance of devices) {
        const deviceReference = { ...deviceInstance };

        data[deviceReference.id] = deviceInstance;
        byId[deviceReference.id] = deviceReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.devices, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override shouldRefreshAfterReconnect({ api, disconnectDurationMs }: ReconnectArgs): boolean {
    return true;
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.devices.on('device.create', this.handleCreate);
    api.devices.on('device.update', this.handleUpdate);
    api.devices.on('device.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.devices.off('device.create', this.handleCreate);
    api.devices.off('device.update', this.handleUpdate);
    api.devices.off('device.delete', this.handleDelete);
  }

  getInstanceFromCache(device: { id: string }) {
    return this.getCache()[device.id];
  }

  getCache(): { [key: string]: Device } {
    const api = this.getApi();
    const cache = api.devices.__cache['device'];
    return cache;
  }

  handleCreate = (createdDevice: Device) => {
    this.DEBUG && console.info(`create:device:${createdDevice.id}`);
    const state = this.get();
    const deviceInstance = this.getInstanceFromCache(createdDevice);

    if (deviceInstance == null) return;

    const deviceReference = { ...deviceInstance };

    this.set({
      data: {
        ...state.data,
        [createdDevice.id]: deviceInstance,
      },
      byId: {
        ...state.byId,
        [createdDevice.id]: deviceReference,
      },
    });
  };

  handleUpdate = (updatedDevice: Device) => {
    this.DEBUG && console.info(`update:device:${updatedDevice.id}`);

    this.addToQueue(updatedDevice.id, updatedDevice);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedDevice] of Object.entries(updateQueue)) {
        const deviceInstance = this.getInstanceFromCache(queuedDevice);

        if (state.data == null) {
          this.DEBUG && console.info(`update:device:missing-data`);
          continue;
        }

        if (deviceInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:device:unknown:${id}`);
          continue;
        }

        const deviceReference = { ...deviceInstance };

        nextById[id] = deviceReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:device:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedDevice: Device) => {
    this.DEBUG && console.info(`delete:device:${deletedDevice.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:device:missing-data`);
      return;
    }

    const deviceInstance = state.data[deletedDevice.id];

    if (deviceInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[deviceInstance.id];
    delete nextById[deviceInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const devices = Object.values(cache ?? {});
    const data: DevicesData = {};
    const byId: DevicesById = {};

    for (const deviceInstance of devices) {
      const deviceReference = { ...deviceInstance };
      data[deviceReference.id] = deviceInstance;
      byId[deviceReference.id] = deviceReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }

  updateOneOptimistic(id: string, update: Partial<DeviceProps>) {
    const state = this.get();
    if (state.byId?.[id] == null) return;
    this.set({
      byId: { ...state.byId, [id]: { ...state.byId[id], ...update } },
    });
  }

  restoreOne(id: string) {
    const state = this.get();
    const deviceInstance = this.getInstanceFromCache({ id });
    if (state.byId == null || deviceInstance == null) return;
    this.set({
      byId: { ...state.byId, [id]: { ...deviceInstance } },
    });
  }
}

export const devicesStore = new DevicesStore();
